import React from "react";
import moment from "moment";
import { useParams, Redirect } from "react-router-dom";
import { Tabs, Tab } from "react-bootstrap";

import Breadcrumbs from "../../../components/Breadcrumbs";
import core from "../../../vendors/core-api";
import APIComponent from "../../../components/API";
import Datatable from "react-data-table-component";
import {
  DANGER_COLOR,
  PRIMARY_COLOR,
  RESERVATION_COLOR,
} from "../../../constants";
import { Button, Row } from "react-bootstrap";
import PacmanLoading from "../../../components/widgets/PacmanLoading/PacmanLoading";
import { SVGIcon } from "../../../components/Base";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { isEmpty, debounce } from "lodash";
import { capitalize } from "@material-ui/core";
import { eachDayOfInterval } from "date-fns";
import { InventoryModal } from "./InventoryModal";
import EventsMetrics from "./EventMetrics";
import EventsSalesMetrics from "./SalesMetrics";

const MySwal = withReactContent(Swal);

const calculateTotalCapacity = (row) => {
  if (!row.tickets || row.tickets.length === 0) {
    return 0;
  }

  const numberOfDays = eachDayOfInterval({
    start: new Date(row.start_date),
    end: new Date(row.end_date),
  }).length;

  const totalCapacityPerDay = row.tickets.reduce((total, ticket) => {
    const timeSlotsCount =
      row.time_slots && row.time_slots.length > 0
        ? ticket.time_slots?.length || 0
        : 1;
    return total + ticket.capacity * timeSlotsCount;
  }, 0);

  return totalCapacityPerDay * numberOfDays;
};

export default function Page(props) {
  const { store_id = "" } = useParams();
  return <MainComponent id={store_id} {...props} />;
}

class MainComponent extends APIComponent {
  constructor(props) {
    super(props);

    this.state = {
      showSplashScreen: true,
      redirectTo: "",
      brand_name: "Brands",
      store: {},
      groupName: "",
      title: "Events",
      activeTab: "list",
      ongoingEvents: { docs: [], totalDocs: 0, page: 1, limit: 10 },
      upcomingEvents: { docs: [], totalDocs: 0, page: 1, limit: 10 },
      pastEvents: { docs: [], totalDocs: 0, page: 1, limit: 10 },
      ongoingStatus: "idle",
      upcomingStatus: "idle",
      pastStatus: "idle",
      isModalOpen: false,
      selectedEvent: {},
      currentSearch: "",
    };

    this.api = core("pu").get();
    this.data_url = `/store/${props.id}/event`;
    this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
  }

  async componentDidMount() {
    this._isMounted = true;
    const { currentSearch } = this.state;
    this.getData("ongoing", 1, 10, currentSearch);
    this.getData("upcoming", 1, 10, currentSearch);
    this.getData("past", 1, 10, currentSearch);
  }

  async getData(status, page = 1, limit = 10, search = "") {
    if (this.data_url !== "") {
      const statusStateKey = `${status}Events`;
      const statusLoadingKey = `${status}Status`;

      this._isMounted && this.setState({ [statusLoadingKey]: "fetching" });
      this.api &&
        this.api
          .get(
            `${this.data_url}?status=${status}&page=${page}&limit=${limit}&search=${search}`,
          )
          .then(({ data }) => {
            if (isEmpty(this.state.store)) {
              this.setState({
                store: data.store,
                groupName: data.store.brand.client.name,
              });
            }
            this.setState({
              [statusStateKey]: data.events,
              [statusLoadingKey]: "fetched",
            });
          })
          .catch((error) => {
            this._isMounted &&
              this.setState({
                error: error,
                showSwalError: true,
                [statusLoadingKey]: "error",
              });
            this.handleOnDataFetchError();
          });
    } else {
      this.afterComponentDidMount();
    }
  }

  handleSearchInputChange = debounce(async (value) => {
    this.setState({ currentSearch: value }, () => {
      this.getData("ongoing", 1, 10, value);
      this.getData("upcoming", 1, 10, value);
      this.getData("past", 1, 10, value);
    });
  }, 500);

  async onEventDelete(event_id) {
    const { store } = this.state;
    if (!isEmpty(event_id)) {
      this.api &&
        this.api
          .put(
            {
              url: `/store/${this.props.id}/delete-event/${event_id}`,
            },
            {
              headers: {
                Authorization: `Basic ${window.btoa(store.brand.brand_url)}`,
              },
              baseURL: this.api.getBaseURL(),
            },
          )
          .then(({ data }) => {
            if (data) {
              MySwal.fire({
                icon: "success",
                title: "Success!",
                text: "Successfully deleted event!",
                confirmButtonColor: PRIMARY_COLOR,
                confirmButtonText: "Ok",
              }).then((result) => {
                if (result && result.isConfirmed) {
                  const { currentSearch } = this.state;
                  this.getData("ongoing", 1, 10, currentSearch);
                  this.getData("upcoming", 1, 10, currentSearch);
                  this.getData("past", 1, 10, currentSearch);
                }
              });
            }
          })
          .catch((error) => {
            this._isMounted &&
              this.setState({ error: error, showSwalError: true });
            this.handleOnDataFetchError();
          });
    }
  }

  getColumns = (category) => {
    const { store } = this.state;

    const actionsColumn = {
      name: "Actions",
      center: true,
      sortable: false,
      selector: (row) => row._id,
      format: (row) => (
        <Button
          variant="link"
          className="btn-icon btn-sm"
          onClick={() => {
            MySwal.fire({
              icon: "warning",
              title: "Warning!",
              text: "Are you sure you want to delete this event?",
              showCancelButton: true,
              confirmButtonColor: DANGER_COLOR,
              confirmButtonText: "Delete",
            }).then((result) => {
              if (result && result.isConfirmed) {
                this.onEventDelete(row._id);
              }
            });
          }}
        >
          <SVGIcon
            variant="info"
            icon={"General/Trash"}
            title={"Delete Cart"}
          />
        </Button>
      ),
    };

    const baseColumns = [
      {
        name: "Event Name",
        sortable: true,
        selector: (row) => row.name,
        width: "20%",
        format: (row) => (
          <span
            className="text-info cursor-pointer"
            onClick={() => {
              this.setState({
                redirectTo: `/stores/${store._id}/event/${row._id}`,
              });
            }}
          >
            {row.name}
          </span>
        ),
      },
      {
        name: "Event Date",
        width: "20%",
        selector: (row) => {
          if (category === "ongoing") {
            return moment().format("MMM D, YYYY");
          }
          const startDate = moment(row.event_day);
          const endDate = moment(row.end_date);
          if (startDate.isSame(endDate, "day")) {
            return startDate.format("MMM D, YYYY");
          }
          return `${startDate.format("MMM D")} - ${endDate.format(
            "MMM D, YYYY",
          )}`;
        },
        sortable: true,
      },
      {
        name: "Event Time",
        width: "20%",
        selector: (row) =>
          row.time_slots
            .map(
              (slot) =>
                `${moment(slot.start_time).format("h:mm A")} - ${moment(
                  slot.end_time,
                ).format("h:mm A")}`,
            )
            .join(" | "),
      },
      {
        name: "Status",
        center: true,
        selector: (row) => capitalize(row.status),
        format: (row) => (
          <span
            style={{ width: 70 }}
            className={`badge badge-pill text-capitalize badge-${
              row.status === "private" ? "light" : "info"
            }`}
          >
            {capitalize(row.status)}
          </span>
        ),
        sortable: true,
      },
    ];

    const inventoryColumn = {
      name: "Inventory",
      center: true,
      sortable: false,
      selector: (row) => {
        const totalCapacity = calculateTotalCapacity(row);
        return `${row.inventory || 0} / ${
          totalCapacity === 0 ? "-" : totalCapacity
        }`;
      },
      format: (row) => {
        const totalCapacity = calculateTotalCapacity(row);
        return (
          <Button
            variant="link-info"
            className="btn-icon mx-4"
            onClick={() => {
              this.setState({
                isModalOpen: true,
                selectedEvent: row,
              });
            }}
            disabled={!row.inventory}
          >
            {`${row.inventory || 0} / ${
              totalCapacity === 0 ? "-" : totalCapacity
            }`}
          </Button>
        );
      },
    };

    const attendanceColumn = {
      name: "Attendance",
      center: true,
      selector: (row) =>
        `${row.total_scanned || 0}/${row.total_confirmed || 0}`,
      sortable: true,
    };

    const scannedColumn = {
      name: "Scanned",
      center: true,
      selector: (row) =>
        `${row.total_scanned || 0}/${row.total_purchased || 0}`,
      sortable: true,
    };

    const rsvpConfirmedColumn = {
      name: "RSVP (Confirmed)",
      center: true,
      selector: (row) => row.total_confirmed || 0,
      sortable: true,
    };

    const rsvpInvitedColumn = {
      name: "RSVP (Invited)",
      center: true,
      selector: (row) => row.total_invited || 0,
      sortable: true,
    };

    const rsvpRejectedColumn = {
      name: "RSVP (Rejected)",
      center: true,
      selector: (row) => row.total_rejected || 0,
      sortable: true,
    };

    switch (category) {
      case "ongoing":
        return [...baseColumns, scannedColumn, actionsColumn];
      case "upcoming":
        return [
          ...baseColumns,
          rsvpConfirmedColumn,
          rsvpInvitedColumn,
          rsvpRejectedColumn,
          inventoryColumn,
          actionsColumn,
        ];
      case "past":
        return [
          ...baseColumns,
          rsvpConfirmedColumn,
          attendanceColumn,
          inventoryColumn,
          actionsColumn,
        ];
      default:
        return [];
    }
  };

  render() {
    const {
      redirectTo,
      store,
      groupName,
      ongoingEvents,
      upcomingEvents,
      pastEvents,
      ongoingStatus,
      upcomingStatus,
      pastStatus,
      isModalOpen,
      selectedEvent,
      currentSearch,
    } = this.state;

    return (
      <>
        {redirectTo !== "" ? <Redirect to={redirectTo} /> : <></>}
        <div className="row">
          <div className="col-md-9 col-sm-12">
            <Breadcrumbs>
              <Breadcrumbs.Item text={groupName} />
              <Breadcrumbs.Item text="Events" link={`/events`} />
              <Breadcrumbs.Item
                text={store.name}
                link="#!"
                textVariant="text-info"
                active={"true"}
              />
            </Breadcrumbs>
          </div>
        </div>
        <Row className="justify-content-space-between align-items-center px-8 py-6">
          <Row className="m-0">
            <h1>Events</h1>
          </Row>
          <Row className="justify-content-end">
            {this.state.activeTab === "list" && (
              <div className="input-group mr-4" style={{ width: "50%" }}>
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    <i className="flaticon-search"></i>
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => {
                    this.handleSearchInputChange(e.target.value);
                  }}
                />
              </div>
            )}

            <Button
              style={{ top: 0, right: 0 }}
              variant="info"
              onClick={() =>
                this.props.history.push(`/stores/${this.props.id}/create-event`)
              }
            >
              + Create New Event
            </Button>
          </Row>
        </Row>
        <style>
          {`
                  .purple-tabs .nav-link {
                    color: #464E5F;
                  }
                  .purple-tabs .nav-link.active, .purple-tabs .nav-item.show .nav-link {
                    color: ${RESERVATION_COLOR};
                    border-color: #dee2e6 #dee2e6 #fff;
                    border-bottom: 3px solid ${RESERVATION_COLOR};
                  }
                  .purple-tabs .nav-link:not(.active):hover {
                    color: ${RESERVATION_COLOR};
                  }
                     .purple-tabs .nav-link.active:hover {
                                color: ${RESERVATION_COLOR};
                              }
                `}
        </style>
        <Tabs
          defaultActiveKey="list"
          id="event-page-tabs"
          activeKey={this.state.activeTab}
          onSelect={(k) => this.setState({ activeTab: k })}
          className="mb-3 purple-tabs"
        >
          <Tab eventKey="metrics" title="Metrics">
            <EventsMetrics store={store} />
          </Tab>
          <Tab eventKey="list" title="Event Lists">
            <EventsSalesMetrics store={store} />
            <div className="d-flex flex-column">
              <div style={{ overflowX: "auto" }}>
                <div className="p-4">
                  <h3 className="font-weight-bolder mb-4">On Going</h3>
                  <Datatable
                    columns={this.getColumns("ongoing")}
                    data={ongoingEvents.docs}
                    progressPending={ongoingStatus === "fetching"}
                    pagination
                    paginationServer
                    paginationTotalRows={ongoingEvents.totalDocs}
                    paginationPerPage={ongoingEvents.limit}
                    paginationDefaultPage={ongoingEvents.page}
                    onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                      this.getData(
                        "ongoing",
                        currentPage,
                        currentRowsPerPage,
                        currentSearch,
                      );
                    }}
                    onChangePage={(page) => {
                      this.getData(
                        "ongoing",
                        page,
                        ongoingEvents.limit,
                        currentSearch,
                      );
                    }}
                    progressComponent={
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <PacmanLoading removeBackgroundColor />
                      </div>
                    }
                    noHeader
                  />
                  <h3 className="font-weight-bolder my-4">Upcoming</h3>
                  <Datatable
                    columns={this.getColumns("upcoming")}
                    data={upcomingEvents.docs}
                    progressPending={upcomingStatus === "fetching"}
                    pagination
                    paginationServer
                    paginationTotalRows={upcomingEvents.totalDocs}
                    paginationPerPage={upcomingEvents.limit}
                    paginationDefaultPage={upcomingEvents.page}
                    onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                      this.getData(
                        "upcoming",
                        currentPage,
                        currentRowsPerPage,
                        currentSearch,
                      );
                    }}
                    onChangePage={(page) => {
                      this.getData(
                        "upcoming",
                        page,
                        upcomingEvents.limit,
                        currentSearch,
                      );
                    }}
                    progressComponent={
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <PacmanLoading removeBackgroundColor />
                      </div>
                    }
                    noHeader
                  />
                  <h3 className="font-weight-bolder my-4">Past Events</h3>
                  <Datatable
                    columns={this.getColumns("past")}
                    data={pastEvents.docs}
                    progressPending={pastStatus === "fetching"}
                    pagination
                    paginationServer
                    paginationTotalRows={pastEvents.totalDocs}
                    paginationPerPage={pastEvents.limit}
                    paginationDefaultPage={pastEvents.page}
                    onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                      this.getData(
                        "past",
                        currentPage,
                        currentRowsPerPage,
                        currentSearch,
                      );
                    }}
                    onChangePage={(page) => {
                      this.getData(
                        "past",
                        page,
                        pastEvents.limit,
                        currentSearch,
                      );
                    }}
                    progressComponent={
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <PacmanLoading removeBackgroundColor />
                      </div>
                    }
                    noHeader
                  />
                </div>
              </div>
            </div>
          </Tab>
        </Tabs>
        {isModalOpen && (
          <InventoryModal
            show={isModalOpen}
            event={selectedEvent}
            api={this.api}
            handleError={this.handleOnDataFetchError}
            onHide={() => this.setState({ isModalOpen: false })}
          />
        )}
      </>
    );
  }
}
